import { AsyncValidatorFn, FormControl, ValidatorFn } from '@angular/forms';
import { Type } from '@angular/core';
import { SortDirection } from '../../models/models.swagger.generated';
import { TranslateService } from '@ngx-translate/core';
import { IAdaptorService } from '../syncfusion/adaptor-service.interface';

type Primitive = string | number | boolean | bigint | symbol | null | undefined | Date;
type PrevDepth = [never, 0, 1, 2, 3, 4, 5];

type NonFunctionKey<T extends object> = {
  [K in keyof T & string]: T[K] extends (...args: any[]) => any ? never : K;
}[keyof T & string];

type PathInternal<T extends object, Depth extends number = 2> = [Depth] extends [never]
  ? never
  : {
      [K in NonFunctionKey<T>]:
        NonNullable<T[K]> extends Primitive
          ? K
          : NonNullable<T[K]> extends readonly (infer TArrayItem)[]
            ? TArrayItem extends object
              ? K | `${K}.${PathInternal<TArrayItem, PrevDepth[Depth]>}`
              : K
            : NonNullable<T[K]> extends object
              ? K | `${K}.${PathInternal<NonNullable<T[K]>, PrevDepth[Depth]>}`
              : K;
    }[NonFunctionKey<T>];

export type DynamicModelKey<T extends object> = NonFunctionKey<T>;
export type DynamicFieldPath<T extends object> = PathInternal<T, 2>;

export type DynamicFilterKind = 'string' | 'number' | 'date' | 'boolean' | 'enum' | 'reference';

export type DynamicControlKind =
  | 'text'
  | 'email'
  | 'number'
  | 'date'
  | 'checkbox'
  | 'textarea'
  | 'enum-select'
  | 'reference-select'
  | 'custom';

export interface DynamicFilterOption {
  value: string | number | boolean;
  textKey?: string;
  text?: string;
}

export interface DynamicEnumOptions {
  enumType: any; // the enum type itself, used to generate options
  /**
   * The enum type name, e.g. 'Role', 'ApplicationType'.
   * Used to derive the default translation prefix: `i18n.shared.enums.<enumName>.`
   */
  enumName: string;
  /**
   * Explicit translation key prefix, e.g. 'i18n.shared.roles.'.
   * When provided, overrides the default `i18n.shared.enums.<name>.` convention.
   */
  translationKeyPrefix?: string;
  excludeValues?: (string | number | boolean)[]; // enum values to exclude from the filter options
}

export interface DynamicFilterConfig {
  kind: DynamicFilterKind;
  operators?: string[];
  options?: DynamicFilterOption[];
  enumOptions?: DynamicEnumOptions;
  reference?: {
    idField: string;
    textField: string;
    serviceToken?: string;
  };
}

export interface DynamicGridColumnDefinition<TList> {
  field: DynamicFieldPath<TList & object>;
  headerKey: string;
  type?: 'string' | 'number' | 'date' | 'boolean';
  width?: string;
  visible?: boolean;
  clipMode?: 'Clip' | 'EllipsisWithTooltip' | 'Ellipsis';
  sortable?: boolean;
  allowFiltering?: boolean;   // default true
  filter?: DynamicFilterConfig;
  formatValue?: (row: TList, translateService: TranslateService) => string;
}

export interface DynamicReferenceConfig {
  serviceType: Type<IAdaptorService>;
  searchField: string;
  textField: string;
  valueField: string;
  /** Maps a nested object on the source entity to provide the initial autocomplete display value without loading from the service.
   *  `sourceField` is the field on the source data containing the related object (e.g. 'client').
   *  The `textField` and `valueField` from this config are used to extract `name` and `id` from it. */
  initialValueMapping?: {
    sourceField: string;
  };
}

export interface DynamicFormFieldDefinition<TModel extends object, TValue = unknown> {
  key: DynamicModelKey<TModel>;
  labelKey: string;
  control: DynamicControlKind;
  defaultValue?: TValue;
  options?: DynamicFilterOption[];
  enumOptions?: DynamicEnumOptions;
  referenceConfig?: DynamicReferenceConfig;
  placeholderKey?: string;
  validators?: ValidatorFn[];
  asyncValidators?: AsyncValidatorFn[];
  colMd?: 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  hiddenOnAdd?: boolean;
  hiddenOnEdit?: boolean;
  readOnlyOnAdd?: boolean;
  readOnlyOnEdit?: boolean;
  section?: string;
}

export interface DynamicFormLayoutRow<TModel extends object> {
  fields: DynamicModelKey<TModel>[];
}

export interface DynamicEntityDefinition<TList extends object, TCreate extends object, TUpdate extends object> {
  entityName: string;
  primaryKey: DynamicModelKey<TList>;
  activeField?: DynamicModelKey<TList>;
  defaultSort?: {
    field: string;
    direction: SortDirection;
  };
  grid: {
    searchFields: DynamicFieldPath<TList>[];
    columns: DynamicGridColumnDefinition<TList>[];
  };
  form: {
    fields: DynamicFormFieldDefinition<TUpdate>[];
    layout: DynamicFormLayoutRow<TUpdate>[];
  };
  mapToCreate: (formValue: TUpdate) => TCreate;
  mapToUpdate: (formValue: TUpdate) => TUpdate;
}

// ──────────────────────────────────────────────────────────────
// Derived FormGroup shape from the edit model
// ──────────────────────────────────────────────────────────────

/**
 * Derives a strongly-typed `FormGroup` control map from an edit model.
 *
 * Each non-function property of `TModel` becomes a `FormControl<TModel[K] | null>`, matching
 * the nullable controls created by `createCrudFormGroup`. This is the type that should be
 * passed as the form-control map to `DynamicGridBaseComponent` (and to `GridBaseComponent` /
 * `FormGroup<T>`), so the form type is always in sync with the definition's `TUpdate` model
 * instead of being hand-maintained.
 *
 * Function members (e.g. `init`, `toJSON` on generated DTO classes) are excluded via
 * `NonFunctionKey`, so only real data fields become controls.
 */
export type DynamicFormType<TModel extends object> = {
  [K in NonFunctionKey<TModel>]: FormControl<TModel[K] | null>;
};

/**
 * Derives the form type for a given dynamic entity definition.
 * Resolves to `DynamicFormType<TUpdate>` for the definition's update model.
 */
export type DynamicEntityFormType<TDefinition extends DynamicEntityDefinition<any, any, any>> =
  TDefinition extends DynamicEntityDefinition<any, any, infer TUpdate>
    ? DynamicFormType<TUpdate>
    : never;

/**
 * Marker type used by `DynamicGridBaseComponent` to signal that the form type should be
 * derived from the entity definition instead of being passed explicitly. Pass this as
 * the first generic argument (or omit it, since it is the default) and the component will
 * resolve `DynamicEntityFormType<TDefinition>` automatically.
 */
export interface DynamicDerivedFormTypeMarker { }

// ──────────────────────────────────────────────────────────────
// Enum translation prefix resolution
// ──────────────────────────────────────────────────────────────

const DEFAULT_ENUM_TRANSLATION_PREFIX = 'i18n.shared.enums.';

/**
 * Resolves the translation prefix for an enum type.
 *
 * If `translationKeyPrefix` is explicitly set, it is used as-is.
 * Otherwise, the default prefix `i18n.shared.enums.<name>.` is derived from the mandatory `name` field.
 *
 * @param enumOptions The enum options containing enumType and enumName
 * @returns The resolved translation prefix string
 */
export function resolveEnumTranslationPrefix(enumOptions: DynamicEnumOptions | undefined | null): string {
  if (!enumOptions) {
    return '';
  }
  if (enumOptions.translationKeyPrefix) {
    return enumOptions.translationKeyPrefix;
  }
  return `${DEFAULT_ENUM_TRANSLATION_PREFIX}${enumOptions.enumName}.`;
}

// ──────────────────────────────────────────────────────────────
// Column definition helpers — reduce per-column boilerplate
// ──────────────────────────────────────────────────────────────

/**
 * Creates a standard enum column definition with automatic `formatValue`,
 * filter configuration, and sensible defaults.
 *
 * Before (verbose):
 * ```ts
 * {
 *   field: 'applicationType',
 *   headerKey: 'i18n.base.user-management.column-headers.applicationType',
 *   type: 'string',
 *   clipMode: 'EllipsisWithTooltip',
 *   filter: {
 *     kind: 'enum',
 *     enumOptions: {
 *       enumType: ApplicationType,
 *       enumName: 'ApplicationType',
 *       translationKeyPrefix: 'i18n.shared.applicationTypes.',
 *       excludeValues: [],
 *     }
 *   },
 * }
 * ```
 *
 * After (concise — uses default prefix `i18n.shared.enums.ApplicationType.`):
 * ```ts
 * enumColumn<UserInfoDTO>('applicationType', 'i18n.base.user-management.column-headers.applicationType', {
 *   enumType: ApplicationType,
 *   enumName: 'ApplicationType',
 * })
 * ```
 *
 * Or with explicit prefix override:
 * ```ts
 * enumColumn<UserInfoDTO>('applicationType', 'i18n.base.user-management.column-headers.applicationType', {
 *   enumType: ApplicationType,
 *   translationKeyPrefix: 'i18n.shared.applicationTypes.',
 * })
 * ```
 */
export function enumColumn<TList extends object>(
  field: DynamicFieldPath<TList>,
  headerKey: string,
  config: {
    enumType: any;
    /** The enum type name, e.g. 'Role'. Used for the default translation prefix `i18n.shared.enums.<enumName>.` */
    enumName: string;
    /** Explicit prefix override. When provided, enumName will be ignored.` */
    translationKeyPrefix?: string;
    excludeValues?: (string | number | boolean)[];
    width?: string;
    clipMode?: 'Clip' | 'EllipsisWithTooltip' | 'Ellipsis';
  },
  overrides?: Partial<DynamicGridColumnDefinition<TList>>,
): DynamicGridColumnDefinition<TList> {
  const enumOptions: DynamicEnumOptions = {
    enumType: config.enumType,
    enumName: config.enumName,
    translationKeyPrefix: config.translationKeyPrefix,
    excludeValues: config.excludeValues ?? [],
  };

  const prefix = resolveEnumTranslationPrefix(enumOptions);

  const formatValue = ((row: any, ts: TranslateService) => {
          const val = row?.[field as string];
          if (val == null) return '';
          // Handle array values (e.g. roles[])
          if (Array.isArray(val)) {
            return val.map((v: any) => ts.instant(prefix + config.enumType[v])).join(', ');
          }
          return ts.instant(prefix + config.enumType[val]);
        }) as any;

  return {
    field,
    headerKey,
    type: 'string',
    clipMode: config.clipMode ?? 'EllipsisWithTooltip',
    width: config.width,
    filter: {
      kind: 'enum',
      enumOptions,
    },
    formatValue,
    ...overrides,
  };
}

/**
 * Creates a standard boolean column definition with filter options.
 *
 * Before:
 * ```ts
 * {
 *   field: 'enabled',
 *   headerKey: 'i18n.base.user-management.column-headers.enabled',
 *   type: 'boolean',
 *   filter: {
 *     kind: 'boolean',
 *     options: [
 *       { value: true, textKey: 'i18n.shared.fields.active' },
 *       { value: false, textKey: 'i18n.shared.inactive' },
 *     ],
 *   },
 * }
 * ```
 *
 * After:
 * ```ts
 * booleanColumn<ClientDTO>('isActive', 'i18n.shared.fields.active')
 * ```
 */
export function booleanColumn<TList extends object>(
  field: DynamicFieldPath<TList>,
  headerKey: string,
  options?: { trueKey?: string; falseKey?: string },
  overrides?: Partial<DynamicGridColumnDefinition<TList>>,
): DynamicGridColumnDefinition<TList> {
  return {
    field,
    headerKey,
    type: 'boolean',
    filter: {
      kind: 'boolean',
      options: [
        { value: true, textKey: options?.trueKey ?? 'i18n.shared.fields.active' },
        { value: false, textKey: options?.falseKey ?? 'i18n.shared.inactive' },
      ],
    },
    ...overrides,
  };
}

/**
 * Creates a standard text column with sensible defaults.
 *
 * @param field     The field path
 * @param headerKey The i18n key for the column header
 * @param filter    Filter kind — defaults to `'string'`
 */
export function textColumn<TList extends object>(
  field: DynamicFieldPath<TList>,
  headerKey: string,
  overrides?: Partial<DynamicGridColumnDefinition<TList>>,
): DynamicGridColumnDefinition<TList> {
  return {
    field,
    headerKey,
    type: 'string',
    clipMode: 'EllipsisWithTooltip',
    filter: { kind: 'string' },
    ...overrides,
  };
}

// ──────────────────────────────────────────────────────────────
// Definition factory with automatic enum formatValue wiring
// ──────────────────────────────────────────────────────────────

/**
 * Creates a dynamic entity definition with automatic enum `formatValue`
 * generation for any enum column that doesn't already provide one.
 *
 * Also resolves `translationKeyPrefix` for all enum options that omit it.
 */
export function defineDynamicEntityDefinition<TList extends object, TCreate extends object, TUpdate extends object>(
  definition: DynamicEntityDefinition<TList, TCreate, TUpdate>
): DynamicEntityDefinition<TList, TCreate, TUpdate> {
  // Auto-wire enum columns that lack a formatValue
  for (const column of definition.grid.columns) {
    if (column.filter?.kind === 'enum' && column.filter.enumOptions) {
      const enumOpts = column.filter.enumOptions;

      // Auto-generate formatValue if not provided
      if (!column.formatValue) {
        const prefix = resolveEnumTranslationPrefix(enumOpts);
        const enumType = enumOpts.enumType;
        const fieldPath = column.field as string;

        column.formatValue = ((row: any, ts: TranslateService) => {
          const val = row?.[fieldPath];
          if (val == null) return '';
          // Handle array values (e.g. roles[])
          if (Array.isArray(val)) {
            return val.map((v: any) => ts.instant(prefix + enumType[v])).join(', ');
          }
          return ts.instant(prefix + enumType[val]);
        }) as any;
      }
    }
  }

  // Also resolve translation prefixes for form enum fields
  for (const field of definition.form.fields) {
    if (field.enumOptions && !field.enumOptions.translationKeyPrefix) {
      field.enumOptions.translationKeyPrefix = resolveEnumTranslationPrefix(field.enumOptions);
    }
  }

  return definition;
}
